/**
 * brandName — the current host's white-label name, or the 'Symbols
 * Workspace' default. The ONE place that reads `root.whitelabelBrand`
 * for a document title (WS-DOCUMENT-TITLE-SUFFIX-BAKED-INTO-I18N-1); a
 * handful of pages compose their own title string around a runtime value
 * (a person's name, a document's own title) and call this directly instead
 * of `pageTitle`, which assumes an i18n key.
 *
 * `function`, not an arrow — see `pageTitle` below for why.
 */
export function brandName() {
  const el = this && typeof this.call === 'function' ? this : null
  const branded = el?.state?.root?.whitelabelBrand?.name
  if (branded) return branded
  // The comm host (workspace docs/COMM_HOST.md) carries its own product name
  // (`shell.comm.brand`). Host test restated — every export is serialized alone.
  let comm = false
  try {
    const host = String((window.location && window.location.hostname) || '').toLowerCase()
    comm =
      /(^|\.)comm?\.symbols\.app$/.test(host) ||
      window.localStorage.getItem('symbols_force_comm') === '1' ||
      (window.location.search || '').includes('comm=1')
  } catch (_) {
    comm = false
  }
  if (comm && el) {
    const name = el.call('polyglot', 'shell.comm.brand')
    if (typeof name === 'string' && name && name !== 'shell.comm.brand') return name
  }
  return 'Symbols Workspace'
}

/**
 * pageTitle — the ONE seam between a page's i18n title and the document
 * <title>, so a white-label host can brand every page without a literal
 * baked into each i18n VALUE (WS-DOCUMENT-TITLE-SUFFIX-BAKED-INTO-I18N-1).
 *
 * `el.call('pageTitle', key)` resolves through `context.functions` and
 * invokes `method.call(element, ...args)` (element/src/methods.js) — the
 * same binding setLang.js documents at length, so this is a plain
 * `export function`, never an arrow: an arrow's `this` stays the module
 * scope and the brand read below would always miss.
 *
 * `key` is looked up through the existing `polyglot` call, so the i18n
 * VALUES stay plain page titles ("Account", "Activity scoring / Admin") —
 * this is the only place the brand suffix is appended, and the only place
 * that needs to change for a new white-label host to see its own name here.
 */
export function pageTitle(key) {
  const el = this && typeof this.call === 'function' ? this : null
  const base = el ? el.call('polyglot', key) : key
  const brand = brandName.call(el)
  return base ? `${base} / ${brand}` : brand
}

// Helmet accepts both a metadata factory and an object whose title is a
// factory. Resolve both at the read site so pane chrome follows the current
// language and brand. A failing app title must not take the pane down.
export function resolvePageMetadataTitle(metadata) {
  const el = this
  try {
    const resolved = typeof metadata === 'function' ? metadata(el, el?.state) : metadata
    const title = resolved?.title
    const value = typeof title === 'function' ? title(el, el?.state) : title
    return typeof value === 'string' ? value : null
  } catch {
    return null
  }
}
